import { ReactNode, CSSProperties, useEffect } from 'react';

// ─── Badge ────────────────────────────────────────────────────────────────────

type BadgeVariant = 'green' | 'amber' | 'blue' | 'red' | 'gray' | 'locked';

const BADGE_STYLES: Record<BadgeVariant, CSSProperties> = {
  green: { background: 'var(--color-green-bg)', color: 'var(--color-green-text)' },
  amber: { background: 'var(--color-amber-bg)', color: 'var(--color-amber-text)' },
  blue: { background: 'var(--color-blue-bg)', color: 'var(--color-blue-text)' },
  red: { background: 'var(--color-red-bg)', color: 'var(--color-red-text)' },
  gray: { background: 'var(--color-border)', color: 'var(--color-text-muted)' },
  locked: { background: 'var(--color-border)', color: 'var(--color-text-hint)', border: '0.5px solid var(--color-border-strong)', fontSize: 10 },
};

export function Badge({ variant, children }: { variant: BadgeVariant; children: ReactNode }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4, ...BADGE_STYLES[variant] }}>
      {children}
    </span>
  );
}

// ─── Status chip (with dot) ───────────────────────────────────────────────────

type ChipStatus = 'Shipped' | 'WIP' | 'Planned' | 'Canceled';
const CHIP_MAP: Record<ChipStatus, { bg: string; color: string; dot: string }> = {
  Shipped: { bg: 'var(--color-green-bg)', color: 'var(--color-green-text)', dot: 'var(--color-green)' },
  WIP: { bg: 'var(--color-amber-bg)', color: 'var(--color-amber-text)', dot: 'var(--color-amber)' },
  Planned: { bg: 'var(--color-border)', color: 'var(--color-text-muted)', dot: 'var(--color-text-hint)' },
  Canceled: { bg: 'var(--color-red-bg)', color: 'var(--color-red-text)', dot: 'var(--color-red)' },
};

export function StatusChip({ status }: { status: ChipStatus | string }) {
  const s = (
    status === 'Completed'  ? 'Shipped'
    : status === 'In Progress' ? 'WIP'
    : status
  ) as ChipStatus;
  const c = CHIP_MAP[s] ?? CHIP_MAP['Planned'];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4, background: c.bg, color: c.color }}>
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      {s}
    </span>
  );
}

// ─── Metric card ──────────────────────────────────────────────────────────────

export function MetricCard({ label, value, sub, accent, onClick }: { label: string; value: ReactNode; sub?: ReactNode; accent?: 'green' | 'amber' | 'blue'; onClick?: () => void }) {
  const accentColor = accent === 'green' ? 'var(--color-green)' : accent === 'amber' ? 'var(--color-amber)' : accent === 'blue' ? 'var(--color-blue)' : 'var(--color-text)';
  const clickable = !!onClick;
  return (
    <div
      onClick={onClick}
      onMouseEnter={clickable ? (e) => { (e.currentTarget as HTMLElement).style.background = 'var(--color-border-strong)'; } : undefined}
      onMouseLeave={clickable ? (e) => { (e.currentTarget as HTMLElement).style.background = 'var(--color-border)'; } : undefined}
      style={{
        background: 'var(--color-border)',
        borderRadius: 'var(--radius-md)',
        padding: '12px 14px',
        cursor: clickable ? 'pointer' : undefined,
        transition: clickable ? 'background .1s' : undefined,
        position: 'relative',
      }}
      title={clickable ? 'Click to drill down' : undefined}
    >
      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>{label}</span>
        {clickable && <span style={{ fontSize: 10, color: 'var(--color-blue)' }}>↗</span>}
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, color: accentColor }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

export function ProgressBar({ pct, color = 'var(--color-blue)' }: { pct: number; color?: string }) {
  return (
    <div style={{ height: 4, background: 'var(--color-border)', borderRadius: 2, overflow: 'hidden', marginTop: 8 }}>
      <div style={{ height: '100%', width: `${Math.min(100, pct)}%`, background: color, borderRadius: 2, transition: 'width .3s' }} />
    </div>
  );
}

// ─── Tier progress strip ──────────────────────────────────────────────────────

export function TierProgress({ value, tiers }: { value: number; tiers: Array<{ threshold_amount: number; tier: number }> }) {
  const max = tiers[tiers.length - 1]?.threshold_amount ?? 1;
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <ProgressBar pct={pct} color="var(--color-green)" />
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--color-text-hint)', marginTop: 4 }}>
        {tiers.map((t) => (
          <span key={t.tier}>T{t.tier}: ${(t.threshold_amount / 1000).toFixed(0)}k</span>
        ))}
      </div>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ background: 'var(--color-surface)', border: '0.5px solid var(--color-border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', ...style }}>
      {children}
    </div>
  );
}

export function CardHeader({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ padding: '10px 16px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 8, ...style }}>
      {children}
    </div>
  );
}

// ─── Table ────────────────────────────────────────────────────────────────────

export function Table({ children }: { children: ReactNode }) {
  return <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>{children}</table>;
}

export function Th({ children, right, sticky }: { children: ReactNode; right?: boolean; sticky?: boolean }) {
  return (
    <th style={{
      padding: '7px 16px', textAlign: right ? 'right' : 'left',
      fontSize: 10, fontWeight: 500, color: 'var(--color-text-muted)',
      borderBottom: '0.5px solid var(--color-border)',
      whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em',
      // Sticky requires the scrolling ancestor to have overflow:auto. Pair
      // with a solid background so the header doesn't show rows scrolling
      // through it; z-index keeps it above the tbody on z-stack.
      ...(sticky && {
        position: 'sticky' as const, top: 0, zIndex: 1,
        background: 'var(--color-surface)',
      }),
    }}>
      {children}
    </th>
  );
}

/**
 * Clickable column header that cycles sort direction on click. Render inside
 * a <thead><tr> like a normal <Th>. The hook `useTableSort` handles the
 * `sortKey/dir/setSort` wiring — pass its output here.
 *
 * Uses "columnKey" (not "key") since JSX reserves `key`.
 */
export function SortableTh({
  columnKey, sortKey, dir, setSort, right, sticky, children,
}: {
  columnKey: string;
  sortKey: string | null;
  dir: 'asc' | 'desc';
  setSort: (key: string) => void;
  right?: boolean;
  /** Opt-in: pin the header to the top of its scrolling container so
   *  long tables keep the column labels in view. Caller must wrap the
   *  table in a parent with `overflow: auto` and a bounded height. */
  sticky?: boolean;
  children: ReactNode;
}) {
  const active = sortKey === columnKey;
  const arrow = !active ? '↕' : dir === 'asc' ? '↑' : '↓';
  return (
    <th
      onClick={() => setSort(columnKey)}
      style={{
        padding: '7px 16px', textAlign: right ? 'right' : 'left',
        fontSize: 10, fontWeight: 500,
        color: active ? 'var(--color-text)' : 'var(--color-text-muted)',
        borderBottom: '0.5px solid var(--color-border)',
        whiteSpace: 'nowrap', textTransform: 'uppercase', letterSpacing: '.04em',
        cursor: 'pointer', userSelect: 'none',
        ...(sticky && {
          position: 'sticky' as const, top: 0, zIndex: 1,
          background: 'var(--color-surface)',
        }),
      }}
      title="Click to sort"
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {children}
        <span style={{ opacity: active ? 1 : 0.35, fontSize: 10 }}>{arrow}</span>
      </span>
    </th>
  );
}

export function Tr({ children, onClick, selected, style }: { children: ReactNode; onClick?: () => void; selected?: boolean; style?: CSSProperties }) {
  // Selected (clicked) state wins over caller-supplied background; caller's
  // background applies in the resting state (e.g. amber-tinted "flagged"
  // rows on the billing preview).
  const restingBg = (style?.background as string | undefined) ?? undefined;
  return (
    <tr
      onClick={onClick}
      style={{
        borderBottom: '0.5px solid var(--color-border)',
        cursor: onClick ? 'pointer' : undefined,
        transition: 'background .1s',
        ...style,
        background: selected ? 'var(--color-blue-bg)' : restingBg,
      }}
      onMouseEnter={onClick ? (e) => { if (!selected) (e.currentTarget as HTMLElement).style.background = 'var(--color-border)'; } : undefined}
      onMouseLeave={onClick ? (e) => { (e.currentTarget as HTMLElement).style.background = selected ? 'var(--color-blue-bg)' : (restingBg ?? ''); } : undefined}
    >
      {children}
    </tr>
  );
}

export function Td({ children, right, muted, bold, style }: { children: ReactNode; right?: boolean; muted?: boolean; bold?: boolean; style?: CSSProperties }) {
  return (
    <td style={{ padding: '9px 16px', verticalAlign: 'middle', textAlign: right ? 'right' : 'left', color: muted ? 'var(--color-text-muted)' : undefined, fontWeight: bold ? 500 : undefined, ...style }}>
      {children}
    </td>
  );
}

// ─── Issue-type pill ─────────────────────────────────────────────────────────
// A compact inline marker that distinguishes Overheads from Deliverables in
// lists. Deliverable is the default/normal case so it renders quietly; Overhead
// gets a slightly louder accent so execs can spot the "other" category at a
// glance without having to read the cell value.

export function IssueTypeBadge({ issueType }: { issueType: 'Deliverable' | 'Overhead' }) {
  const isOverhead = issueType === 'Overhead';
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 9,
      fontWeight: 500,
      padding: '1px 5px',
      borderRadius: 3,
      letterSpacing: '.03em',
      marginRight: 6,
      textTransform: 'uppercase',
      background: isOverhead ? 'var(--color-amber-bg)' : 'var(--color-border)',
      color:      isOverhead ? 'var(--color-amber-text)' : 'var(--color-text-muted)',
    }}>
      {isOverhead ? 'OH' : 'Del'}
    </span>
  );
}

// ─── Modal ────────────────────────────────────────────────────────────────────
// Centered overlay dialog. Used for drill-downs from aggregated numbers so
// execs and team leaders can answer "what's behind this figure?" without
// losing context. Dismiss via backdrop click, close button, or Esc.

export function Modal({
  title,
  subtitle,
  onClose,
  children,
  width = 900,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    // Lock body scroll while the modal is open.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(16, 24, 40, 0.4)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: '80px 20px 20px',
        overflowY: 'auto',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: '0 20px 50px rgba(16, 24, 40, 0.25)',
          width: '100%', maxWidth: width,
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '0.5px solid var(--color-border)', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
            {subtitle && <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 3 }}>{subtitle}</div>}
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', fontSize: 22, lineHeight: 1, cursor: 'pointer', padding: '0 4px' }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Drill-down panel ─────────────────────────────────────────────────────────

export function DrillPanel({ title, meta, onClose, children }: { title: string; meta?: ReactNode; onClose: () => void; children: ReactNode }) {
  return (
    <div style={{ background: 'var(--color-surface)', border: '0.5px solid #B5D4F4', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
      <div style={{ padding: '10px 16px', background: 'var(--color-blue-bg)', borderBottom: '0.5px solid #B5D4F4', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-blue-text)' }}>{title}</div>
          {meta && <div style={{ fontSize: 11, color: 'var(--color-blue)', marginTop: 2 }}>{meta}</div>}
        </div>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--color-blue)', fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '2px 4px' }}>×</button>
      </div>
      {children}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function EmptyState({ message = 'No data found' }: { message?: string }) {
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
      {message}
    </div>
  );
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner() {
  return (
    <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
      Loading…
    </div>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────────────────

interface FilterBarProps {
  children: ReactNode;
  /** Show an "Updating…" chip + optional Apply/Clear buttons on the right.
   *  Every field is optional so existing simple call sites still work. */
  isFetching?: boolean;
  dirty?: boolean;
  onApply?: () => void;
  onClear?: () => void;
}

export function FilterBar({ children, isFetching, dirty, onApply, onClear }: FilterBarProps) {
  const hasControls = !!(isFetching || onApply || onClear);
  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border)', flexWrap: 'wrap' }}>
      {children}
      {hasControls && (
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          {isFetching && <UpdatingBadge />}
          {onClear && <ActionButton onClick={onClear} variant="default">Clear filters</ActionButton>}
          {onApply && (
            <ActionButton onClick={onApply} variant="primary" disabled={!dirty}>
              {dirty ? 'Apply' : 'Applied'}
            </ActionButton>
          )}
        </span>
      )}
    </div>
  );
}

/** Small inline "Updating…" chip shown while isFetching is true on a
 *  filter-bar — signals that the visible rows may be stale for a moment. */
export function UpdatingBadge() {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 11, color: 'var(--color-text-muted)',
    }}>
      <SpinnerDot />
      Updating…
    </span>
  );
}

export function SpinnerDot({ size = 10 }: { size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size, height: size,
        borderRadius: '50%',
        border: '1.5px solid var(--color-border-strong)',
        borderTopColor: 'var(--color-blue)',
        animation: 'orp-spin 0.8s linear infinite',
      }}
    />
  );
}

// Inject keyframes once per app load — keeps <SpinnerDot /> self-contained.
if (typeof document !== 'undefined' && !document.getElementById('orp-spin-kf')) {
  const s = document.createElement('style');
  s.id = 'orp-spin-kf';
  s.textContent = `@keyframes orp-spin { to { transform: rotate(360deg) } }`;
  document.head.appendChild(s);
}

export function Select({ value, onChange, children }: { value: string; onChange: (v: string) => void; children: ReactNode }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ fontSize: 11, padding: '3px 6px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--color-border-strong)', background: 'var(--color-surface)', color: 'var(--color-text)', cursor: 'pointer' }}
    >
      {children}
    </select>
  );
}

export function ActionButton({ onClick, variant = 'default', children, disabled }: { onClick: () => void; variant?: 'default' | 'primary' | 'danger'; children: ReactNode; disabled?: boolean }) {
  const styles: Record<string, CSSProperties> = {
    default: { background: 'var(--color-surface)', color: 'var(--color-text)', border: '0.5px solid var(--color-border-strong)' },
    primary: { background: 'var(--color-blue)', color: '#fff', border: '0.5px solid var(--color-blue)' },
    danger: { background: 'var(--color-red-bg)', color: 'var(--color-red-text)', border: '0.5px solid var(--color-red-text)' },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ fontSize: 11, padding: '4px 10px', borderRadius: 'var(--radius-sm)', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: disabled ? 0.45 : 1, ...styles[variant] }}
    >
      {children}
    </button>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────

export function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--color-text-muted)', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 6 }}>
      {children}
    </div>
  );
}

// ─── Avatar ───────────────────────────────────────────────────────────────────

const AV_COLORS = [
  { bg: '#E6F1FB', color: '#0C447C' },
  { bg: '#E1F5EE', color: '#085041' },
  { bg: '#EEEDFE', color: '#3C3489' },
  { bg: '#FAEEDA', color: '#633806' },
];

export function Avatar({ initials, size = 28, index = 0 }: { initials: string; size?: number; index?: number }) {
  const c = AV_COLORS[index % AV_COLORS.length];
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: c.bg, color: c.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, fontWeight: 500, flexShrink: 0 }}>
      {initials}
    </div>
  );
}

export function initials(name: string): string {
  return name.split(' ').map((p) => p[0]).join('').slice(0, 2).toUpperCase();
}

// ─── Period picker ──────────────────────────────────────────────────────────
//
// Presets + a custom range for any page that shows a time-bounded view. The
// onChange callback receives {from, to, monthsCount}; downstream code that
// paginates by month uses monthsCount, downstream code that wants the raw
// range uses from/to. Custom mode reveals two date inputs.

export type PeriodPreset = 'today' | 'week' | 'month' | 'quarter' | 'last3' | 'last6' | 'last12' | 'last24' | 'custom';

export interface PeriodValue {
  preset: PeriodPreset;
  /** Inclusive lower bound, YYYY-MM-DD. */
  from: string;
  /** Inclusive upper bound, YYYY-MM-DD. */
  to: string;
  /** Distinct calendar months touched by the range — for APIs that bucket by
   *  month (scorecard, team rollup). At least 1. */
  monthsCount: number;
}

function todayYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthsSpanned(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return Math.max(1, (ty - fy) * 12 + (tm - fm) + 1);
}

/** Resolve a preset into a concrete {from, to, monthsCount}. Custom requires
 *  the caller to pass its own from/to. */
export function resolvePeriod(preset: PeriodPreset, customFrom?: string, customTo?: string): PeriodValue {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const ymd = (dt: Date) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  const firstOfMonth = (yy: number, mm: number) => new Date(yy, mm, 1);
  const lastOfMonth  = (yy: number, mm: number) => new Date(yy, mm + 1, 0);

  switch (preset) {
    case 'today': {
      const t = ymd(now);
      return { preset, from: t, to: t, monthsCount: 1 };
    }
    case 'week': {
      // ISO-ish: Monday → today.
      const day = (now.getDay() + 6) % 7; // Mon=0
      const mon = new Date(y, m, d - day);
      return { preset, from: ymd(mon), to: ymd(now), monthsCount: monthsSpanned(ymd(mon), ymd(now)) };
    }
    case 'month': {
      return { preset, from: ymd(firstOfMonth(y, m)), to: ymd(lastOfMonth(y, m)), monthsCount: 1 };
    }
    case 'quarter': {
      const qStart = Math.floor(m / 3) * 3;
      return {
        preset,
        from: ymd(firstOfMonth(y, qStart)),
        to: ymd(lastOfMonth(y, qStart + 2)),
        monthsCount: 3,
      };
    }
    case 'last3':
    case 'last6':
    case 'last12':
    case 'last24': {
      const n = preset === 'last3' ? 3 : preset === 'last6' ? 6 : preset === 'last12' ? 12 : 24;
      const from = firstOfMonth(y, m - (n - 1));
      return { preset, from: ymd(from), to: ymd(lastOfMonth(y, m)), monthsCount: n };
    }
    case 'custom': {
      const from = customFrom && /^\d{4}-\d{2}-\d{2}$/.test(customFrom) ? customFrom : ymd(firstOfMonth(y, m - 5));
      const to   = customTo   && /^\d{4}-\d{2}-\d{2}$/.test(customTo)   ? customTo   : ymd(lastOfMonth(y, m));
      return { preset, from, to, monthsCount: monthsSpanned(from, to) };
    }
  }
}

const PRESET_OPTIONS: Array<{ value: PeriodPreset; label: string }> = [
  { value: 'today',   label: 'Today' },
  { value: 'week',    label: 'This week' },
  { value: 'month',   label: 'This month' },
  { value: 'quarter', label: 'This quarter' },
  { value: 'last3',   label: 'Last 3 months' },
  { value: 'last6',   label: 'Last 6 months' },
  { value: 'last12',  label: 'Last 12 months' },
  { value: 'last24',  label: 'Last 24 months' },
  { value: 'custom',  label: 'Custom range…' },
];

export function PeriodPicker({
  value, onChange,
}: {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
}) {
  const isCustom = value.preset === 'custom';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Period</span>
      <Select
        value={value.preset}
        onChange={(v) => {
          const p = v as PeriodPreset;
          const next = p === 'custom'
            ? resolvePeriod('custom', value.from, value.to)
            : resolvePeriod(p);
          onChange(next);
        }}
      >
        {PRESET_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </Select>
      {isCustom && (
        <>
          <input
            type="date"
            value={value.from}
            max={value.to || todayYmd()}
            onChange={(e) => onChange(resolvePeriod('custom', e.target.value, value.to))}
            style={periodInputStyle}
          />
          <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>→</span>
          <input
            type="date"
            value={value.to}
            min={value.from}
            onChange={(e) => onChange(resolvePeriod('custom', value.from, e.target.value))}
            style={periodInputStyle}
          />
        </>
      )}
    </span>
  );
}

const periodInputStyle: React.CSSProperties = {
  fontSize: 11, padding: '3px 6px',
  border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--color-surface)',
  color: 'var(--color-text)',
};

/**
 * Renders a user's display name with an italic "(inactive)" tag when the
 * user is flagged inactive — either by the admin or because Jira hard-
 * deleted them (in which case display_name is "(deactivated or unknown)").
 * Use anywhere a bare user name would otherwise be ambiguous about status.
 */
export function UserName({
  name, active = true, style,
}: {
  name: string;
  active?: boolean;
  style?: React.CSSProperties;
}) {
  const isPlaceholder = name === '(deactivated or unknown)';
  if (active && !isPlaceholder) {
    return <span style={style}>{name}</span>;
  }
  return (
    <span style={{ ...style, opacity: 0.7 }}>
      <span style={{ textDecoration: isPlaceholder ? 'none' : undefined }}>
        {isPlaceholder ? 'Unknown user' : name}
      </span>
      <span style={{ fontStyle: 'italic', color: 'var(--color-text-muted)', fontSize: 11, marginLeft: 4 }}>
        ({isPlaceholder ? 'unresolved' : 'inactive'})
      </span>
    </span>
  );
}
